import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Search, Clock } from "lucide-react";
import { api, media, img } from "../lib/api";
import { PILLARS, pillarMeta } from "../lib/constants";
import { usePlayer } from "../context/PlayerContext";

export default function Episodes() {
  const [episodes, setEpisodes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const { track, isPlaying, playTrack } = usePlayer();

  useEffect(() => {
    api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return episodes.filter((e) => {
      const matchPillar = filter === "all" || e.pillar === filter;
      const matchQ =
        !q ||
        e.title.toLowerCase().includes(q.toLowerCase()) ||
        (e.guest || "").toLowerCase().includes(q.toLowerCase());
      return matchPillar && matchQ;
    });
  }, [episodes, filter, q]);

  return (
    <section id="episodes" className="relative py-24 bg-[#0C1424]" data-testid="episodes-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#0066FF] mb-3">New Episodes Weekly</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Episodes</h2>
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search episodes, guests..."
              data-testid="episode-search-input"
              className="w-full rounded-full bg-[#050B14] border border-[#0066FF]/25 pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#0066FF]"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} color="#F5F7FA" label="All" />
          {PILLARS.map((p) => (
            <FilterBtn
              key={p.id}
              active={filter === p.id}
              onClick={() => setFilter(p.id)}
              color={p.color}
              label={p.short}
              testid={`episode-filter-${p.id}`}
            />
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="episodes-list-container">
          {filtered.map((ep, i) => {
            const meta = pillarMeta(ep.pillar);
            const isCurrent = track && track.id === ep.id && isPlaying;
            return (
              <motion.div
                key={ep.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                data-testid={`episode-card-${ep.id}`}
                className="group rounded-xl overflow-hidden border border-[#0066FF]/15 bg-[#121D33] hover:border-[#0066FF]/50 transition-colors"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={img(ep.cover_file_id, ep.cover_url)}
                    alt={ep.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121D33] via-transparent to-transparent" />
                  <span
                    className="absolute top-3 left-3 font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
                    style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                  >
                    {meta.short}
                  </span>
                  <button
                    onClick={() => playTrack(ep)}
                    data-testid={`episode-play-${ep.id}`}
                    className="absolute bottom-3 right-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors blue-box-glow"
                  >
                    {isCurrent ? (
                      <Pause className="h-6 w-6 text-white fill-white" />
                    ) : (
                      <Play className="h-6 w-6 text-white fill-white ml-0.5" />
                    )}
                  </button>
                </div>
                <div className="p-5">
                  <h3 className="font-cond font-bold uppercase text-lg leading-tight mb-1">{ep.title}</h3>
                  {ep.guest && <div className="text-xs text-[#0066FF] font-mono2 mb-2">with {ep.guest}</div>}
                  <p className="text-sm text-[#94A3B8] line-clamp-2 mb-3">{ep.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-[#94A3B8] font-mono2">
                    <Clock className="h-3.5 w-3.5" /> {ep.duration || "—"}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-[#94A3B8] font-mono2 uppercase text-sm">No episodes found</div>
        )}
      </div>
    </section>
  );
}

const FilterBtn = ({ active, onClick, color, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="font-cond font-semibold uppercase text-sm tracking-wide px-4 py-2 rounded-full border transition-colors"
    style={
      active
        ? { background: color, color: "#050B14", borderColor: color }
        : { background: "transparent", color: "#94A3B8", borderColor: "rgba(0,102,255,0.25)" }
    }
  >
    {label}
  </button>
);
